import React, { useEffect, useRef, useState } from 'react';
import { useWallet } from '../hooks/useWallet';
import Reveal from '../components/Reveal';
import AgentMintCard from '../components/AgentMintCard';
import PriceLadder from '../components/PriceLadder';
import useTiers from '../hooks/useTiers';
import { Link } from 'react-router-dom';
import { contract, getLogs, errMsg, chainNow, KIND, TIER } from '../lib';

// Ships created by one settled mint: MintSettled(pid, firstId, count) -> Ships.getShip for each id (tier/serial are on-chain).
async function shipsOfMint(firstId, count) {
  const ships = contract('Ships');
  return Promise.all(Array.from({ length: Number(count) }, async (_, i) => {
    const id = BigInt(firstId) + BigInt(i); const s = await ships.getShip(id);
    return { id, kind: KIND[Number(s.kind)], tier: TIER[Number(s.tier)], serial: Number(s.serial) };
  }));
}

// Forced-tier preview, e.g. /mint?revealDemo=common,p-rare,epic,L07,L13 (p- = pirate, Lnn = Legendary serial nn).
// Purely visual: no chain reads or transactions.
function demoShips(spec) {
  const toks = (spec === '1' || spec === '' ? 'common,p-rare,epic,common,L07' : spec).split(',');
  return toks.map((t, i) => {
    const m = /^L(\d{1,2})$/i.exec(t.trim());
    if (m) { const n = Math.min(20, Math.max(1, Number(m[1]))); return { id: BigInt(9000 + i), kind: n <= 10 ? 'Miner' : 'Pirate', tier: 'Legendary', serial: n }; }
    const pir = /^p-/i.test(t.trim()); const tier = t.trim().replace(/^p-/i, '').toLowerCase();
    const T = TIER.find((x) => x.toLowerCase() === tier && x !== 'Legendary') || 'Common';
    return { id: BigInt(9000 + i), kind: pir ? 'Pirate' : 'Miner', tier: T, serial: 0 };
  });
}

function Mint() {
  const [info, setInfo] = useState(null);
  const [pend, setPend] = useState([]);
  const [now, setNow] = useState(Date.now() / 1000);
  const { account, send, nonce, setStatus } = useWallet();
  const [reveal, setReveal] = useState(null); // { ships, title, demo, share }
  const ladder = useTiers(nonce);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const spec = q.get('revealDemo');
    if (spec !== null) setReveal({ ships: demoShips(spec), title: 'Reveal preview', demo: true, share: `/r/demo/${(spec || 'common,p-rare,epic,common,L07').replace(/[^A-Za-z0-9,-]/g, '')}` });
    // ?agent=1 (old links): the agent mint card is now always on screen, nothing to open.
    // Read-only replay of any settled mint straight from chain (no wallet): /mint?revealMint=<pid>
    const rp = q.get('revealMint');
    if (rp && /^\d+$/.test(rp)) replay(BigInt(rp)).catch(() => {});
  }, []);

  useEffect(() => {
    const g = contract('Genesis');
    chainNow().then(setNow).catch(() => {});
    (async () => {
      const [maxSupply, mintStart, maxPerHandle, maxPerTx] = await Promise.all([g.maxSupply(), g.mintStart(), g.maxPerHandle(), g.maxPerTx()]);
      setInfo({ maxSupply: Number(maxSupply), mintStart: Number(mintStart), maxPerHandle: Number(maxPerHandle), maxPerTx: Number(maxPerTx) });
    })().catch(() => {});
  }, [nonce]);

  // Mints of the wallet connected in the header (if any). Reveal is automatic (inside the VRF answer):
  // poll mintStatus (0 none, 1 drawing, 2 drawn, 3 revealed) and play the reveal when a mint we saw unrevealed turns 3.
  // The buttons are fallbacks only: settleReady() if a drawn mint is stuck, retry(pid) if VRF never answers.
  const [tick, setTick] = useState(0);
  const seen = useRef(new Set()); // pids seen unrevealed in this session
  const known = useRef(null); // pids present at first load (older mints: no auto-play)
  useEffect(() => {
    if (!account) { setPend([]); known.current = null; return undefined; }
    let alive = true; let timer;
    const g = contract('Genesis');
    (async () => {
      const [evs, rd, nowS] = await Promise.all([getLogs(g, g.filters.MintRequested(null, account)), g.retryDelay(), chainNow()]);
      const rows = await Promise.all(evs.map(async (ev) => {
        const pid = ev.args[0];
        const [st, p] = await Promise.all([g.mintStatus(pid), g.pending(pid)]);
        return { pid, count: Number(p.count), status: Number(st), canRetry: Number(st) === 1 && nowS >= Number(p.requestedAt) + Number(rd) };
      }));
      if (!alive) return;
      rows.reverse(); setPend(rows);
      const first = known.current === null || known.current.acct !== account;
      if (first) { known.current = new Set(rows.map((r) => String(r.pid))); known.current.acct = account; }
      for (const r of rows) {
        const k = String(r.pid);
        const fresh = !first && !known.current.has(k); // minted while this page was open (e.g. by the agent)
        if (fresh) known.current.add(k);
        if (r.status !== 3) seen.current.add(k);
        else if (seen.current.has(k) || fresh) { seen.current.delete(k); replay(r.pid).catch(() => {}); }
      }
      // keep watching: fast while a draw is open, slower otherwise (to catch the agent's next mint)
      timer = setTimeout(() => setTick((t) => t + 1), rows.some((r) => r.status !== 3) ? 4000 : 12000);
    })().catch(() => { if (alive) timer = setTimeout(() => setTick((t) => t + 1), 8000); });
    return () => { alive = false; clearTimeout(timer); };
  }, [account, nonce, tick]);

  const open = !info || now >= info.mintStart;
  const max = info ? Math.min(info.maxPerTx, info.maxPerHandle) : 10;

  const openReveal = async (pid, firstId, count, block) => {
    try { setReveal({ ships: await shipsOfMint(firstId, count), title: `Mint #${pid} · ${count} ship(s)`, share: block ? `/r/${pid}-${block}` : null }); }
    catch (e) { setStatus({ kind: 'err', text: `Load ships: ${errMsg(e)}` }); }
  };
  // Fallbacks only (normally the reveal needs no transaction).
  const settleNow = async () => { await send('Reveal drawn mints', (s) => contract('Genesis', s).settleReady()); setTick((t) => t + 1); };
  const retryDraw = async (pid) => { await send(`Retry draw for mint #${pid}`, (s) => contract('Genesis', s).retry(pid)); setTick((t) => t + 1); };
  const replay = async (pid) => {
    const g = contract('Genesis');
    const [ev] = await getLogs(g, g.filters.MintSettled(pid));
    if (ev) await openReveal(pid, ev.args[1], ev.args[2], ev.blockNumber);
  };

  return (
    <div className="m1">
      <div className="m1-title">
        <h1>Mint Gen0 Ships</h1>
        <p>{(info?.maxSupply ?? 10000).toLocaleString()} ships · 85% Miners / 15% Pirates · random draw by Chainlink VRF</p>
      </div>
      <div className="m1-grid">
        <AgentMintCard L={ladder} max={max} open={open} opensAt={info?.mintStart} />
        <aside className="m1-side">
          <PriceLadder L={ladder} side />
          <div className="m1-after" data-testid="after-mint">
            <h3><span className="m1-num sm">4</span>After you mint</h3>
            <p>Ships reveal automatically in about 10 s, nothing to sign. Keep this page open (wallet connected) to watch, or find them in your <Link to="/dashboard">Fleet</Link>.</p>
            {pend.length > 0 && (
              <div className="m1-pend" data-testid="pending">
                {pend.slice(0, 3).map((p) => (
                  <div key={String(p.pid)}>
                    <span>Mint #{String(p.pid)} · {p.count} ship{p.count === 1 ? '' : 's'} · {['…', 'drawing…', 'revealing…', 'revealed'][p.status]}</span>
                    {p.status === 2 && <button title="Only needed if the automatic reveal is stuck" onClick={settleNow}>Reveal now</button>}
                    {p.canRetry && <button title="The random draw did not answer; request a new one" onClick={() => retryDraw(p.pid)}>Retry draw</button>}
                    {p.status === 3 && <button onClick={() => replay(p.pid)}>View</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>
      {reveal && <Reveal {...reveal} account={account} onClose={() => setReveal(null)} />}
    </div>
  );
}

export default Mint;
